import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Button } from '@openai/apps-sdk-ui/components/Button';
import { Input } from '@openai/apps-sdk-ui/components/Input';
import { Textarea } from '@openai/apps-sdk-ui/components/Textarea';
import { Checkbox } from '@openai/apps-sdk-ui/components/Checkbox';
import { Avatar } from '@openai/apps-sdk-ui/components/Avatar';
import { Badge } from '@openai/apps-sdk-ui/components/Badge';
import {
  Plus,
  Search,
  CheckCircle,
  NotebookCheck,
  ArrowRight,
} from '@openai/apps-sdk-ui/components/Icon';
import type { Task, TaskPatch, User, NewTask, RecurrenceConfig, Project } from '../shared/types';
import { errorMessage } from './api';
import { validatedTimeZone } from '../shared/datetime';
import { RepeatBadge, RepeatControls, repeatChoice, type RepeatChoice } from './Recurrence';
import { Empty, ErrorNotice, Field, Modal, assigneeTone, formatDate, Kbd } from './ui';

export const STATUS_LABEL: Record<Task['status'], string> = {
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Completed',
};
export function Tasks({
  tasks,
  projects,
  users,
  historyUsers = [],
  user,
  updateTask,
  insertCommand,
  setEditor,
}: {
  tasks: Task[];
  projects: Project[];
  users: User[];
  historyUsers?: User[];
  user: User;
  updateTask: (id: string, patch: TaskPatch) => Promise<void>;
  insertCommand: (value: string) => void;
  setEditor: (task: Task | 'new' | null) => void;
}) {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState<string[]>([]);
  const visible = useMemo(
    () =>
      tasks.filter(
        (task) =>
          (filter !== 'mine' || task.assigneeId === user.id) &&
          (filter !== 'done' || task.status === 'done') &&
          (filter !== 'active' || task.status !== 'done') &&
          `${task.title} ${task.description}`.toLowerCase().includes(query.toLowerCase()),
      ),
    [tasks, filter, query, user.id],
  );
  const completed = tasks.filter((task) => task.status === 'done').length;
  async function toggle(task: Task) {
    setPending((v) => [...v, task.id]);
    setError('');
    try {
      await updateTask(task.id, { status: task.status === 'done' ? 'todo' : 'done' });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setPending((v) => v.filter((id) => id !== task.id));
    }
  }
  return (
    <div className="tasks-page page-enter">
      <div className="page-heading">
        <div>
          <span className="eyebrow">YOUR WORK, IN ONE PLACE</span>
          <h1>
            Tasks<span className="heading-count">{tasks.length}</span>
          </h1>
          <p>Make a little progress. Keep everyone in the loop.</p>
        </div>
        <Button color="primary" size="lg" onClick={() => setEditor('new')}>
          <Plus />
          New task<span className="button-shortcut">C</span>
        </Button>
      </div>
      <div className="task-toolbar">
        <div className="tab-list" aria-label="Task filters">
          {[
            ['all', 'All tasks'],
            ['active', 'Active'],
            ['mine', 'Assigned to me'],
            ['done', 'Completed'],
          ].map(([id, title]) => (
            <button
              key={id}
              className={filter === id ? 'active' : ''}
              onClick={() => setFilter(id)}
            >
              {title}
              {id === 'all' && <span>{tasks.length}</span>}
            </button>
          ))}
        </div>
        <Input
          aria-label="Search tasks"
          placeholder="Search tasks…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          startAdornment={<Search />}
          className="task-search"
          size="md"
        />
      </div>
      {error && <ErrorNotice message={error} />}
      <div className="task-list">
        {visible.length === 0 ? (
          tasks.length === 0 ? (
            <Empty
              icon={<NotebookCheck />}
              title="A clear space for what’s next."
              description="Add your first task, or start with a command."
            >
              <Button color="primary" onClick={() => setEditor('new')}>
                <Plus />
                Create a task
              </Button>
              <button className="starter-command" onClick={() => insertCommand('task add ')}>
                <span>❯</span>
                <code>task add</code>
                <span className="muted">your next thing</span>
                <ArrowRight />
              </button>
            </Empty>
          ) : (
            <Empty
              icon={<Search />}
              title="No tasks found"
              description="Try a different search or task filter."
            >
              <Button
                color="secondary"
                variant="outline"
                onClick={() => {
                  setQuery('');
                  setFilter('all');
                }}
              >
                Clear filters
              </Button>
            </Empty>
          )
        ) : (
          ['in_progress', 'todo', 'done'].map((status) => {
            const group = visible.filter((task) => task.status === status);
            if (!group.length) return null;
            return (
              <section className="task-group" key={status}>
                <div className="task-group-heading">
                  <span className={`status-dot ${status}`} />
                  <h2>{STATUS_LABEL[status as Task['status']]}</h2>
                  <span>{group.length}</span>
                </div>
                {group.map((task) => {
                  const assignee = [...users, ...historyUsers].find(
                    (member) => member.id === task.assigneeId,
                  );
                  const tone = assigneeTone(users, task.assigneeId);
                  const overdue =
                    task.dueDate &&
                    task.dueDate < new Date().toLocaleDateString('en-CA') &&
                    task.status !== 'done';
                  return (
                    <div
                      className={`task-row ${task.status === 'done' ? 'completed' : ''}`}
                      data-assignee-tone={tone}
                      key={task.id}
                    >
                      <Checkbox
                        id={`task-${task.id}`}
                        checked={task.status === 'done'}
                        onCheckedChange={() => void toggle(task)}
                        disabled={
                          pending.includes(task.id) || (task.status === 'done' && !!task.recurrence)
                        }
                        label={
                          <span className="sr-only">
                            {task.status === 'done' && task.recurrence
                              ? 'Completed occurrence'
                              : task.status === 'done'
                                ? 'Reopen'
                                : 'Complete'}{' '}
                            {task.title}
                          </span>
                        }
                      />
                      <button className="task-title" onClick={() => setEditor(task)}>
                        <span>{task.title}</span>
                        {task.description && <small>{task.description}</small>}
                        {task.projectId && (
                          <small>
                            Project ·{' '}
                            {projects.find((project) => project.id === task.projectId)?.name ||
                              'Project'}
                          </small>
                        )}
                      </button>
                      <div className="task-meta">
                        <RepeatBadge recurrence={task.recurrence} />
                        {task.priority === 'high' && (
                          <Badge color="warning" variant="soft">
                            High priority
                          </Badge>
                        )}
                        {task.dueDate && (
                          <span className={`task-date ${overdue ? 'overdue' : ''}`}>
                            {overdue && 'Overdue · '}
                            {formatDate(task.dueDate)}
                          </span>
                        )}
                        {assignee ? (
                          <span title={assignee.name}>
                            <Avatar name={assignee.name} size={24} color={tone} />
                          </span>
                        ) : (
                          <span className="unassigned" title="Unassigned">
                            —
                          </span>
                        )}
                        <code className="task-id" title={task.id}>
                          {task.id.slice(0, 6)}
                        </code>
                      </div>
                    </div>
                  );
                })}
              </section>
            );
          })
        )}
      </div>
      {tasks.length > 0 && (
        <footer className="task-footer">
          <span>
            <CheckCircle />
            {completed} of {tasks.length} completed
          </span>
          <span>
            Quick create <Kbd>C</Kbd> <span className="footer-separator">·</span> Command line{' '}
            <Kbd>⌘ K</Kbd>
          </span>
        </footer>
      )}
    </div>
  );
}
export function TaskEditor({
  task,
  latestTask,
  users,
  historyUsers = [],
  projects,
  defaultProjectId = null,
  defaultStatus = 'todo',
  defaultTimeZone,
  close,
  save,
  remove,
  configureRecurrence,
  openCurrentOccurrence,
  followUp,
}: {
  task: Task | null;
  latestTask?: Task;
  users: User[];
  historyUsers?: User[];
  projects: Project[];
  defaultProjectId?: string | null;
  defaultStatus?: Task['status'];
  defaultTimeZone?: string;
  close: () => void;
  save: (task: NewTask | TaskPatch) => Promise<void>;
  remove?: () => void;
  configureRecurrence?: (
    recurrence: RecurrenceConfig | null,
    expectedVersion: number,
  ) => Promise<Task>;
  openCurrentOccurrence?: () => void;
  followUp?: () => void;
}) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const form = useRef<HTMLFormElement>(null);
  const [frequency, setFrequency] = useState<RepeatChoice>(repeatChoice(task?.recurrence));
  const [zone, setZone] = useState(
    task?.recurrence?.timeZone ||
      defaultTimeZone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      'UTC',
  );
  const [status, setStatus] = useState<Task['status']>(task?.status || defaultStatus);
  const [expectedVersion, setExpectedVersion] = useState(task?.version || 0);
  const current = latestTask || task;
  const historical = !!current?.recurrence && current.status === 'done';
  const conflict = !!task && !!latestTask && latestTask.version !== expectedVersion;
  const repeatDirty =
    !!task &&
    (frequency !== repeatChoice(current?.recurrence) ||
      (frequency !== 'off' && !!current?.recurrence && zone !== current.recurrence.timeZone));
  function recurrenceConfig(data: Record<string, FormDataEntryValue>): RecurrenceConfig | null {
    if (frequency === 'off') return null;
    const anchorDate = String(data.dueDate || '');
    if (!anchorDate) throw new Error('Choose a due date before enabling repeat.');
    return { frequency, timeZone: validatedTimeZone(zone), anchorDate, localTime: null };
  }
  async function applyRepeat() {
    if (!configureRecurrence || running.current || historical || conflict) return;
    setError('');
    let recurrence: RecurrenceConfig | null;
    try {
      recurrence = recurrenceConfig(Object.fromEntries(new FormData(form.current!)));
    } catch (error) {
      setError(errorMessage(error));
      return;
    }
    running.current = true;
    setBusy(true);
    try {
      const saved = await configureRecurrence(recurrence, expectedVersion);
      setExpectedVersion(saved.version);
      setFrequency(repeatChoice(saved.recurrence));
      setZone(saved.recurrence?.timeZone || zone);
      if (recurrence && saved.dueDate) {
        const input = form.current?.querySelector<HTMLInputElement>('input[name="dueDate"]');
        if (input) input.value = saved.dueDate;
      }
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (running.current || historical || conflict || repeatDirty) return;
    setError('');
    const data = Object.fromEntries(new FormData(event.currentTarget));
    running.current = true;
    setBusy(true);
    try {
      const recurrence = !task ? recurrenceConfig(data) : null;
      await save({
        title: String(data.title).trim(),
        description: String(data.description),
        priority: data.priority as Task['priority'],
        ...(!task || task.assigneeId !== (String(data.assigneeId) || null)
          ? { assigneeId: String(data.assigneeId) || null }
          : {}),
        dueDate: String(data.dueDate) || null,
        status,
        projectId: String(data.projectId) || null,
        ...(task ? { expectedVersion } : recurrence ? { recurrence } : {}),
      });
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  return (
    <Modal
      title={historical ? 'Completed task occurrence' : task ? 'Task details' : 'Create task'}
      close={() => {
        if (!running.current) close();
      }}
    >
      <form ref={form} onSubmit={submit} className="stack-form" aria-busy={busy}>
        {task && followUp && (
          <Button color="secondary" variant="outline" size="sm" onClick={followUp} disabled={busy}>
            Follow up privately
          </Button>
        )}
        {historical && (
          <div className="repeat-history-notice">
            <RepeatBadge recurrence={current?.recurrence} />
            <p>
              This completed occurrence is history. Open the current occurrence to change or stop
              future repeats.
            </p>
            {openCurrentOccurrence && (
              <Button color="secondary" variant="outline" size="sm" onClick={openCurrentOccurrence}>
                Open current occurrence
              </Button>
            )}
          </div>
        )}
        <Field label="Title">
          <Input
            name="title"
            placeholder="What needs doing?"
            required
            maxLength={240}
            autoFocus
            defaultValue={task?.title || ''}
            size="xl"
            readOnly={busy || historical}
          />
        </Field>
        <Field label="Description">
          <Textarea
            name="description"
            placeholder="Add a little context…"
            maxLength={10000}
            defaultValue={task?.description || ''}
            rows={4}
            readOnly={busy || historical}
          />
        </Field>
        <div className="form-grid">
          <Field label="Status">
            <select
              name="status"
              value={historical ? current!.status : status}
              onChange={(event) => setStatus(event.target.value as Task['status'])}
              disabled={busy || historical}
            >
              {Object.entries(STATUS_LABEL).map(([key, label]) => (
                <option
                  value={key}
                  key={key}
                  disabled={!task && frequency !== 'off' && key === 'done'}
                >
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Priority">
            <select
              name="priority"
              defaultValue={task?.priority || 'normal'}
              disabled={busy || historical}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </Field>
          <Field label="Assigned to">
            <select
              name="assigneeId"
              defaultValue={task?.assigneeId || ''}
              disabled={busy || historical}
            >
              <option value="">Unassigned</option>
              {task?.assigneeId && !users.some((user) => user.id === task.assigneeId) && (
                <option value={task.assigneeId}>
                  {historyUsers.find((user) => user.id === task.assigneeId)?.name ||
                    'Previous assignee'}{' '}
                  (inactive)
                </option>
              )}
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Due date">
            <Input
              name="dueDate"
              type="date"
              defaultValue={task?.dueDate || ''}
              required={frequency !== 'off' && !historical}
              readOnly={busy || historical}
            />
          </Field>
        </div>
        <RepeatControls
          value={frequency}
          onChange={(value) => {
            setFrequency(value);
            if (!task && value !== 'off' && status === 'done') setStatus('todo');
          }}
          busy={busy || conflict}
          existing={!!task}
          active={!!current?.recurrence?.active}
          historical={historical}
          apply={() => void applyRepeat()}
        />
        {frequency !== 'off' && (
          <Field label="Repeat timezone">
            <Input
              value={zone}
              onChange={(event) => setZone(event.target.value)}
              required
              maxLength={100}
              readOnly={busy || historical}
            />
          </Field>
        )}
        {current?.recurrence?.active && !historical && (
          <p className="repeat-occurrence-note">
            Saving the date changes only this occurrence. Apply repeat uses the date above to
            reanchor future occurrences.
          </p>
        )}
        {repeatDirty && !historical && (
          <p className="repeat-pending" role="status">
            Apply repeat changes before saving task details. The repeat action uses the due date
            above.
          </p>
        )}
        <Field label="Project">
          <select
            name="projectId"
            defaultValue={task ? task.projectId || '' : defaultProjectId || ''}
            disabled={busy || historical}
          >
            <option value="">No project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </Field>
        {task && (
          <p className="task-detail-meta">
            Created {new Date(task.createdAt).toLocaleDateString()} <span>·</span>
            <code>{task.id.slice(0, 8)}</code>
          </p>
        )}
        {conflict && !historical && (
          <div className="version-conflict" role="status">
            <p>This task changed elsewhere. Your draft is preserved.</p>
            <details>
              <summary>Review latest saved task</summary>
              <strong>{latestTask!.title}</strong>
              <p>{latestTask!.description}</p>
              <p>
                Due {latestTask!.dueDate || 'without a date'} · {STATUS_LABEL[latestTask!.status]}
              </p>
              <Button
                color="secondary"
                variant="outline"
                size="sm"
                onClick={() => {
                  setExpectedVersion(latestTask!.version);
                }}
              >
                Keep my draft and use latest version
              </Button>
            </details>
          </div>
        )}
        {error && <ErrorNotice message={error} />}
        <div className="modal-actions">
          {remove && !historical && (
            <Button
              color="danger"
              variant="ghost"
              className="editor-delete-action"
              onClick={remove}
              disabled={busy || conflict}
            >
              {current?.recurrence?.active ? 'Delete current task & stop repeat' : 'Delete task'}
            </Button>
          )}
          <div className="spacer" />
          <Button color="secondary" variant="outline" onClick={close} disabled={busy}>
            {historical ? 'Close' : 'Cancel'}
          </Button>
          {!historical && (
            <Button type="submit" color="primary" loading={busy} disabled={conflict || repeatDirty}>
              {task ? 'Save changes' : 'Create task'}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}
